import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";

interface SlotDigitProps {
    digit: string;
    duration: number;
    delay: number;
}

function SlotDigit({ digit, duration, delay }: SlotDigitProps) {
    const isDigit = /\d/.test(digit);
    const [animating, setAnimating] = useState(false);
    const [displayDigit, setDisplayDigit] = useState(digit);
    const prevDigitRef = useRef(digit);
    const prevWasDigitRef = useRef(isDigit);

    useEffect(() => {
        const prevWasDigit = prevWasDigitRef.current;

        // If the character type changed (e.g. space → digit or digit → space),
        // just snap to the new value without animating
        if (!isDigit || !prevWasDigit || digit === prevDigitRef.current) {
            prevDigitRef.current = digit;
            prevWasDigitRef.current = isDigit;
            setDisplayDigit(digit);
            setAnimating(false);
            return;
        }

        setAnimating(true);

        const timeout = setTimeout(() => {
            setDisplayDigit(digit);
            setAnimating(false);
            prevDigitRef.current = digit;
            prevWasDigitRef.current = isDigit;
        }, delay + duration);

        return () => clearTimeout(timeout);
    }, [digit, isDigit, duration, delay]);

    if (!isDigit) {
        return (
            <Box component="span" sx={{
                display: "inline-block",
                height: "1.2em",
                lineHeight: "1.2em",
                verticalAlign: "bottom",
            }}>
                {digit}
            </Box>
        );
    }

    const digitNum = parseInt(displayDigit, 10);
    const safeDigitNum = isNaN(digitNum) ? 0 : digitNum;
    // Build a strip of digits: show several cycles then land on the target
    const cycles = 2;
    const strip: number[] = [];
    for (let c = 0; c < cycles; c++) {
        for (let d = 0; d <= 9; d++) {
            strip.push(d);
        }
    }
    strip.push(safeDigitNum);
    const totalSteps = strip.length;

    return (
        <Box
            component="span"
            sx={{
                display: "inline-block",
                height: "1.2em",
                overflow: "hidden",
                position: "relative",
                verticalAlign: "bottom",
            }}
        >
            <Box
                component="span"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    transition: animating
                        ? `transform ${duration}ms cubic-bezier(0.25, 0.1, 0.25, 1) ${delay}ms`
                        : "none",
                    transform: animating
                        ? `translateY(-${(totalSteps - 1) * 1.2}em)`
                        : "translateY(0)",
                }}
            >
                {animating ? (
                    strip.map((d, i) => (
                        <Box
                            component="span"
                            key={i}
                            sx={{
                                height: "1.2em",
                                lineHeight: "1.2em",
                            }}
                        >
                            {d}
                        </Box>
                    ))
                ) : (
                    <Box
                        component="span"
                        sx={{
                            height: "1.2em",
                            lineHeight: "1.2em",
                        }}
                    >
                        {displayDigit}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

interface SlotCounterProps {
    value: string;
    duration?: number;
}

function SlotCounter({ value, duration = 800 }: SlotCounterProps) {
    const prevValueRef = useRef(value);
    const chars = value.split("");
    const prevChars = prevValueRef.current.split("");

    // Align characters from right-to-left so digits stay matched
    // when the string length changes (e.g. "1 234 kr" → "12 345 kr")
    const maxLen = Math.max(chars.length, prevChars.length);
    const alignedChars: { char: string; key: string }[] = [];

    for (let i = 0; i < maxLen; i++) {
        const fromRight = maxLen - 1 - i;
        const newIdx = chars.length - 1 - fromRight;
        const ch = newIdx >= 0 ? chars[newIdx] : "";
        alignedChars.push({ char: ch, key: `r${fromRight}` });
    }

    // Filter out empty chars (when new value is shorter, which shouldn't normally happen for growing jackpots)
    const visibleChars = alignedChars.filter((c) => c.char !== "");

    // Stagger delay: rightmost digit animates first
    const digitPositions = visibleChars
        .map((c, i) => (/\d/.test(c.char) ? i : -1))
        .filter((i) => i >= 0);

    const maxDigitPos = digitPositions.length - 1;
    const staggerMs = 60;

    // Update prev value ref after render
    useEffect(() => {
        prevValueRef.current = value;
    }, [value]);

    return (
        <Box component="span" sx={{ display: "inline-flex", whiteSpace: "pre" }}>
            {visibleChars.map((c, i) => {
                const digitPos = digitPositions.indexOf(i);
                const delay =
                    digitPos >= 0 ? (maxDigitPos - digitPos) * staggerMs : 0;

                return (
                    <SlotDigit
                        key={c.key}
                        digit={c.char}
                        duration={duration}
                        delay={delay}
                    />
                );
            })}
        </Box>
    );
}

export default SlotCounter;
